import React, { createContext, useContext } from "react";
import type { AnnotationTypeCategory, AnnotationQuality } from "./ANNTypes";
import { AnnotationContextType } from "./ANNTypes";
export interface PendingDocument {
   id: string;
   file: File;
   comment: string;
   uploadResult?: { key: string; url: string };
   annotationType?: AnnotationTypeCategory;
}
export interface PendingNote {
   id: string;
   comment: string;
   text: string;
   annotationType?: AnnotationTypeCategory;
}
export interface ANNEditorState {
   isOpen: boolean;
   contextType?: AnnotationContextType;
   identifier?: string | number;
   stedfaestelseTitle: string;
   stedfaestelseHistoryComment: string;
   stedfaestelseAnnotationType?: AnnotationTypeCategory;
   stedfaestelseQuality?: AnnotationQuality;
   pendingLayers: number[];
   pendingDocuments: PendingDocument[];
   pendingNotes: PendingNote[];
   editSession?: {
      oldAnnotationId?: string;
      datoloebenr?: string;
      originalCreatedAt?: string;
      originalCreatedBy?: string;
      originalCreatedByName?: string;
      initialLayerIds?: number[];
      organizationId?: string;
   };
   saving: boolean;
   error?: string;
}
export const initialANNEditorState = (): ANNEditorState => ({
   isOpen: false,
   contextType: undefined,
   identifier: undefined,
   stedfaestelseTitle: "",
   stedfaestelseHistoryComment: "",
   stedfaestelseAnnotationType: "Stedfæstelse",
   stedfaestelseQuality: undefined,
   pendingLayers: [],
   pendingDocuments: [],
   pendingNotes: [],
   saving: false,
   error: undefined,
});
export enum ANNEditorActionType {
   Open = "Open",
   Close = "Close",
   Reset = "Reset",
   SetStedfaestelseTitle = "SetStedfaestelseTitle",
   SetStedfaestelseHistoryComment = "SetStedfaestelseHistoryComment",
   SetStedfaestelseAnnotationType = "SetStedfaestelseAnnotationType",
   SetStedfaestelseQuality = "SetStedfaestelseQuality",
   AddLayer = "AddLayer",
   RemoveLayer = "RemoveLayer",
   SetLayers = "SetLayers",
   AddDocument = "AddDocument",
   UpdateDocumentComment = "UpdateDocumentComment",
   SetDocumentAnnotationType = "SetDocumentAnnotationType",
   RemoveDocument = "RemoveDocument",
   SetDocumentUploadResult = "SetDocumentUploadResult",
   AddNote = "AddNote",
   UpdateNote = "UpdateNote",
   SetNoteAnnotationType = "SetNoteAnnotationType",
   RemoveNote = "RemoveNote",
   SetEditSession = "SetEditSession",
   ClearEditSession = "ClearEditSession",
   SaveStart = "SaveStart",
   SaveSuccess = "SaveSuccess",
   SaveFailure = "SaveFailure",
}
export type ANNEditorAction =
   | { type: ANNEditorActionType.Open; payload: { contextType: AnnotationContextType; identifier: string | number } }
   | { type: ANNEditorActionType.Close }
   | { type: ANNEditorActionType.Reset }
   | { type: ANNEditorActionType.SetStedfaestelseTitle; payload: string }
   | { type: ANNEditorActionType.SetStedfaestelseHistoryComment; payload: string }
   | { type: ANNEditorActionType.SetStedfaestelseAnnotationType; payload: AnnotationTypeCategory }
   | { type: ANNEditorActionType.SetStedfaestelseQuality; payload: AnnotationQuality | undefined }
   | { type: ANNEditorActionType.AddLayer; payload: number }
   | { type: ANNEditorActionType.RemoveLayer; payload: number }
   | { type: ANNEditorActionType.SetLayers; payload: number[] }
   | { type: ANNEditorActionType.AddDocument; payload: PendingDocument }
   | { type: ANNEditorActionType.UpdateDocumentComment; payload: { id: string; comment: string } }
   | { type: ANNEditorActionType.SetDocumentAnnotationType; payload: { id: string; annotationType: AnnotationTypeCategory } }
   | { type: ANNEditorActionType.RemoveDocument; payload: string }
   | { type: ANNEditorActionType.SetDocumentUploadResult; payload: { id: string; uploadResult: { key: string; url: string } } }
   | { type: ANNEditorActionType.AddNote; payload: PendingNote }
   | { type: ANNEditorActionType.UpdateNote; payload: { id: string; comment?: string; text?: string } }
   | { type: ANNEditorActionType.SetNoteAnnotationType; payload: { id: string; annotationType: AnnotationTypeCategory } }
   | { type: ANNEditorActionType.RemoveNote; payload: string }
   | {
      type: ANNEditorActionType.SetEditSession;
      payload: {
         oldAnnotationId?: string;
         datoloebenr?: string;
         originalCreatedAt?: string;
         originalCreatedBy?: string;
         originalCreatedByName?: string;
         initialLayerIds?: number[];
         organizationId?: string;
      };
   }
   | { type: ANNEditorActionType.ClearEditSession }
   | { type: ANNEditorActionType.SaveStart }
   | { type: ANNEditorActionType.SaveSuccess }
   | { type: ANNEditorActionType.SaveFailure; payload: string };
export function annEditorReducer(state: ANNEditorState, action: ANNEditorAction): ANNEditorState {
   switch (action.type) {
      case ANNEditorActionType.Open:
         return {
            ...initialANNEditorState(),
            isOpen: true,
            contextType: action.payload.contextType,
            identifier: action.payload.identifier,
         };
      case ANNEditorActionType.Close:
         return {
            ...state,
            isOpen: false,
         };
      case ANNEditorActionType.Reset:
         return initialANNEditorState();
      case ANNEditorActionType.SetStedfaestelseTitle:
         return { ...state, stedfaestelseTitle: action.payload };
      case ANNEditorActionType.SetStedfaestelseHistoryComment:
         return { ...state, stedfaestelseHistoryComment: action.payload };
      case ANNEditorActionType.SetStedfaestelseAnnotationType:
         return { ...state, stedfaestelseAnnotationType: action.payload };
      case ANNEditorActionType.SetStedfaestelseQuality:
         return { ...state, stedfaestelseQuality: action.payload };
      case ANNEditorActionType.AddLayer:
         if (state.pendingLayers.includes(action.payload)) {
            return state;
         }
         return {
            ...state,
            pendingLayers: [...state.pendingLayers, action.payload],
         };
      case ANNEditorActionType.RemoveLayer:
         return {
            ...state,
            pendingLayers: state.pendingLayers.filter(id => id !== action.payload),
         };
      case ANNEditorActionType.SetLayers:
         return {
            ...state,
            pendingLayers: action.payload,
         };
      case ANNEditorActionType.AddDocument:
         return {
            ...state,
            pendingDocuments: [...state.pendingDocuments, action.payload],
         };
      case ANNEditorActionType.UpdateDocumentComment:
         return {
            ...state,
            pendingDocuments: state.pendingDocuments.map(d =>
               d.id === action.payload.id ? { ...d, comment: action.payload.comment } : d
            ),
         };
      case ANNEditorActionType.SetDocumentAnnotationType:
         return {
            ...state,
            pendingDocuments: state.pendingDocuments.map(d =>
               d.id === action.payload.id ? { ...d, annotationType: action.payload.annotationType } : d
            ),
         };
      case ANNEditorActionType.RemoveDocument:
         return {
            ...state,
            pendingDocuments: state.pendingDocuments.filter(d => d.id !== action.payload),
         };
      case ANNEditorActionType.SetDocumentUploadResult:
         return {
            ...state,
            pendingDocuments: state.pendingDocuments.map(d =>
               d.id === action.payload.id ? { ...d, uploadResult: action.payload.uploadResult } : d
            ),
         };
      case ANNEditorActionType.AddNote:
         return {
            ...state,
            pendingNotes: [...state.pendingNotes, action.payload],
         };
      case ANNEditorActionType.UpdateNote:
         return {
            ...state,
            pendingNotes: state.pendingNotes.map(n =>
               n.id === action.payload.id
                  ? { ...n, ...(action.payload.comment !== undefined && { comment: action.payload.comment }), ...(action.payload.text !== undefined && { text: action.payload.text }) }
                  : n
            ),
         };
      case ANNEditorActionType.SetNoteAnnotationType:
         return {
            ...state,
            pendingNotes: state.pendingNotes.map(n =>
               n.id === action.payload.id ? { ...n, annotationType: action.payload.annotationType } : n
            ),
         };
      case ANNEditorActionType.RemoveNote:
         return {
            ...state,
            pendingNotes: state.pendingNotes.filter(n => n.id !== action.payload),
         };
      case ANNEditorActionType.SetEditSession:
         return {
            ...state,
            editSession: action.payload,
         };
      case ANNEditorActionType.ClearEditSession:
         return {
            ...state,
            editSession: undefined,
         };
      case ANNEditorActionType.SaveStart:
         return { ...state, saving: true, error: undefined };
      case ANNEditorActionType.SaveSuccess:
         return { ...state, saving: false };
      case ANNEditorActionType.SaveFailure:
         return { ...state, saving: false, error: action.payload };
      default:
         return state;
   }
}
export type ANNEditorDispatch = React.Dispatch<ANNEditorAction>;
export const ANNEditorContext = createContext<{
   state: ANNEditorState;
   dispatch: ANNEditorDispatch;
}>({
   state: initialANNEditorState(),
   dispatch: () => undefined,
});
export function useANNEditorContext() {
   return useContext(ANNEditorContext);
}
